/**
 * Human-agent attachment upload and presigned download client.
 */
import { getBearerAuthHeaders } from "@/lib/authSession";
import { fetchWithAuthRetry } from "@/lib/authedRequest";
import { inboxWorkspaceHeaders } from "@/lib/inboxLiveConfig";

export class AttachmentApiClient {
  /**
   * @param {string} path
   * @param {{ method?: string, workspaceId?: string | null, body?: FormData }} opts
   */
  async _fetchMultipart(path, { method = "POST", workspaceId, body }) {
    // No Content-Type header — the browser sets the multipart boundary. The
    // same FormData is safe to re-send on the auth-retry.
    const buildRequest = () =>
      fetch(new URL(path, (import.meta.env?.VITE_API_BASE_URL || window.location.origin)).toString(), {
        method,
        credentials: "include",
        headers: {
          Accept: "application/json",
          ...getBearerAuthHeaders(),
          ...inboxWorkspaceHeaders(workspaceId),
        },
        body,
      });
    const res = await fetchWithAuthRetry(buildRequest);
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
        if (j?.error) detail = typeof j.error === "string" ? j.error : JSON.stringify(j.error);
      } catch {
        /* ignore */
      }
      const err = new Error(detail || `Request failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  /**
   * @param {string} path
   * @param {{ workspaceId?: string | null, searchParams?: Record<string, string> }} opts
   */
  async _fetchGet(path, { workspaceId, searchParams }) {
    const buildRequest = () => {
      const url = new URL(path, (import.meta.env?.VITE_API_BASE_URL || window.location.origin));
      if (searchParams) {
        for (const [k, v] of Object.entries(searchParams)) {
          if (v) url.searchParams.set(k, v);
        }
      }
      return fetch(url.toString(), {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          ...getBearerAuthHeaders(),
          ...inboxWorkspaceHeaders(workspaceId),
        },
      });
    };
    const res = await fetchWithAuthRetry(buildRequest);
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
        if (j?.error) detail = typeof j.error === "string" ? j.error : JSON.stringify(j.error);
      } catch {
        /* ignore */
      }
      const err = new Error(detail || `Request failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  /**
   * @param {{ sessionId: string, file: File, workspaceId?: string | null }} params
   */
  async uploadLiveChat({ sessionId, file, workspaceId }) {
    const form = new FormData();
    form.append("session_id", sessionId);
    form.append("file", file);
    const payload = await this._fetchMultipart("/api/v1/human-agent/attachments", {
      workspaceId,
      body: form,
    });
    return payload?.attachment ?? payload;
  }

  /**
   * @param {{ ticketId: string, file: File, workspaceId?: string | null }} params
   */
  async uploadTicket({ ticketId, file, workspaceId }) {
    const form = new FormData();
    form.append("file", file);
    const payload = await this._fetchMultipart(
      `/api/v1/human-agent/tickets/${encodeURIComponent(ticketId)}/attachments`,
      { workspaceId, body: form },
    );
    return payload?.attachment ?? payload;
  }

  /**
   * @param {{ attachmentId: string, workspaceId?: string | null, sessionId?: string, context?: 'live_chat' | 'ticket' }} params
   */
  async fetchDownloadUrl({ attachmentId, workspaceId, sessionId, context = "live_chat" }) {
    const sp = { context };
    if (context === "live_chat" && sessionId) sp.session_id = sessionId;
    const payload = await this._fetchGet(
      `/api/v1/human-agent/attachments/${encodeURIComponent(attachmentId)}`,
      { workspaceId, searchParams: sp },
    );
    return payload?.download_url ?? "";
  }
}

export const attachmentApi = new AttachmentApiClient();
